<template>
  <div class="pt-6">
    <div class="flex flex-wrap items-center justify-between mb-3">
      <h2 class="flex-1 text-[16px] font-aktiv-grotesk-medium">
        {{ date !== '' ? formatDate(date) : formatExpType(expType) }}
      </h2>
      <div
        v-if="isSelectingPhotos"
        class="flex font-aktiv-grotesk-regular text-[14px] text-lightblue leading-5 cursor-pointer items-center bg-grey-5 rounded-[100px] px-2 py-1"
        @click="selectAllInGallery"
      >
        Select all
        <img
          v-if="isAllPhotosSelected"
          class="ml-2 md:ml-4 w-[20px] md:w-[28px] h-[20px] md:h-[28px] border border-[#00A857] rounded-full"
          src="/assets/icons/blue-tick.svg"
          alt=""
        />

        <span
          v-else
          class="ml-2 md:ml-4 w-[20px] md:w-[28px] h-[20px] md:h-[28px] bg-white border border-lightblue rounded-full"
        />
      </div>
    </div>

    <div :id="galleryId" class="flex gap-2 flex-wrap hidden">
      <a
        v-for="(photo, key) in formatPhoto(links)"
        :key="key"
        :href="photo.url"
        :data-pswp-download-link="photo.type === 'video' ? photo.videoUrl : photo.url"
        :data-pswp-width="photo.w"
        :data-pswp-height="photo.h"
        :data-pswp-type="photo.type"
        target="_blank"
        rel="noreferrer"
      >
        <img v-if="photo.type === 'video'" :src="photo.videoThumbnailUrl" class="w-full" />
      </a>
    </div>
    <div
      class="tiles grid gap-x-1 gap-y-2 pb-2 auto-rows-fr min-w-[106px] min-h-[106px] md:min-w-[200px] md:min-h-[200px]"
    >
      <label
        v-for="(linkObj, linkIndex) in links"
        :key="linkIndex"
        class="relative cursor-pointer"
        :class="{
          'scale-[0.96] transition-transform duration-200': longPressHoldingIndex === linkIndex,
        }"
        data-test-id="gensapp-photodrop-selectphotos"
        @click="selectPhotos(linkObj.url, linkIndex)"
        @touchstart.passive="onPhotoPointerDown($event, linkObj, linkIndex)"
        @touchmove.passive="onPhotoPointerMove($event)"
        @touchend="onPhotoPointerUp"
        @touchcancel="onPhotoPointerUp"
        @mousedown="onPhotoPointerDown($event, linkObj, linkIndex)"
        @mousemove="onPhotoPointerMove($event)"
        @mouseup="onPhotoPointerUp"
      >
        <div class="relative">
          <img
            class="block w-[106px] h-[106px] md:w-[200px] md:h-[200px] object-cover"
            :src="linkObj.type === 'image' ? linkObj.previewUrl : linkObj.videoThumbnailUrl"
          />
          <div
            v-if="linkObj.type === 'video'"
            class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-2xl"
          >
            <img
              src="../../../public/assets/icons/youtube-player/play-button-dark.svg"
              data-test-id="gensapp-home-kickoff-play-video"
              class="w-10 h-10 lg:w-[48px] lg:h-[48px]"
              @click="selectPhotos(linkObj.url, linkIndex)"
            />
          </div>
        </div>

        <img
          v-if="isSelectingPhotos && isPhotoSelected(linkObj.url)"
          class="absolute block bottom-2 right-2 w-[20px] md:w-[28px] h-[20px] md:h-[28px] border border-[#00A857] rounded-full"
          src="/assets/icons/blue-tick.svg"
          alt=""
        />

        <span
          v-if="isSelectingPhotos && !isPhotoSelected(linkObj.url)"
          class="absolute bottom-2 right-2 block w-[20px] md:w-[28px] h-[20px] md:h-[28px] bg-white border border-lightblue rounded-full"
        />
      </label>
    </div>
    <PhotoLongPressMenu
      :visible="!!longPressedPhoto"
      :photo="longPressedPhoto"
      :is-downloading="isDownloading"
      :is-gic="isGic"
      @download="onLongPressDownload"
      @select="onLongPressSelect"
      @close="closeLongPressMenu"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { isIosNativeOnly } from '@/utils/platform-check';
import { StatusBar, Style } from '@capacitor/status-bar';
import PhotoSwipeLightbox from '../../../node_modules/photoswipe/dist/photoswipe-lightbox.esm';
import PhotoSwipe from '../../../node_modules/photoswipe/dist/photoswipe.esm.js';
import PhotoSwipeVideoPlugin from '../../utils/photoswipe-video-plugin.esm.js';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import mixpanel from '@/utils/mixpanel';
import momentts from 'moment-timezone';
import photoDownloadMixin from '@/mixins/photoDownloadMixin';
import PhotoLongPressMenu from './PhotoLongPressMenu.vue';
import { isGICPage } from '@/utils/conference-type';

export default {
  components: { PhotoLongPressMenu },
  mixins: [photoDownloadMixin],
  props: {
    date: {
      type: String,
      default: '',
    },
    expType: {
      type: String,
      default: '',
    },
    links: {
      type: Array,
      required: true,
    },
    viewportWidth: {
      type: Number,
      required: true,
    },
    // hasTooltip: {
    //   type: Boolean,
    //   default: false,
    // },
  },
  data() {
    return {
      showingTooltip: false,
      tooltipHover: false,

      lightbox: null,
      // Long-press menu state
      longPressedPhoto: null,
      longPressTimer: null,
      longPressStartX: 0,
      longPressStartY: 0,
      longPressHoldingIndex: null,
      longPressConsumed: false,
      options: {
        index: 0,
        pinchToClose: false,
        closeOnScroll: false,
        closeOnVerticalDrag: false,
        preload: [1, 2],
        closeEl: true,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: true,
        shareEl: false,
        counterEl: true,
        arrowEl: false,
        preloaderEl: true,
        spacing: 0.6,
        allowPanToNext: true,
        clickToCloseNonZoomable: false,
        arrowKeys: false,
        getDoubleTapZoom: (isMouseClick, item) => {
          if (isMouseClick) {
            return 1.5;
          } else {
            return item.initialZoomLevel < 0.7 ? 1 : 1.5;
          }
        },

        gallery: '#pictureGallery',
        children: 'a',
        pswpModule: PhotoSwipe,
      },
    };
  },
  computed: {
    ...mapState('photos', ['isSelectingPhotos', 'selectedPhotos']),
    ...mapState('profile', ['id']),
    ...mapState('signup', ['expData']),
    isGic() {
      const _ = this.$route.path;
      return isGICPage();
    },
    isAllPhotosSelected() {
      let isSelected = true;
      this.links.forEach(link => {
        isSelected = isSelected && this.selectedPhotos.includes(link.url);
      });

      return isSelected;
    },
    galleryId() {
      return 'myGallery-' + this.date;
    },
  },
  watch: {
    showingTooltip(value) {
      if (value) {
        window.addEventListener('mousedown', this.outsideClick);
      } else {
        window.removeEventListener('mousedown', this.outsideClick);
      }
    },
    viewportWidth(width) {
      if (width >= 1024) {
        this.showingTooltip = false;
      }
    },
  },
  mounted() {
    const routePath = this.$route.path;
    const lightbox = new PhotoSwipeLightbox(this.options);
    const _videoPlugin = new PhotoSwipeVideoPlugin(lightbox, {
      autoplay: false,
    });
    lightbox.on('uiRegister', function () {
      const logEvent = function (pathName) {
        const pos = pathName.lastIndexOf('/');
        const fileName = pathName.substring(pos + 1);
        try {
          FirebaseAnalytics.logEvent({
            name: 'click_button',
            params: {
              button_name: 'gensapp-photodrop-gallery-download',
              page: 'photoDrop',
              fileName: fileName,
            },
          });
        } catch (e) {
          // noop
        }
        try {
          window.dataLayer.push({
            event: 'gensapp-photodrop-gallery-download-web',
            file_name: fileName,
          });
        } catch (e) {
          // noop
        }
        try {
          mixpanel.track('Photodrop Individual File Downloaded', {
            file_name: fileName,
            download_type: 'single',
            page_url: routePath,
            page: 'photodrop',
          });
        } catch (e) {
          // noop
        }
      };
      lightbox.pswp.ui.registerElement({
        name: 'download-button',
        order: 8,
        isButton: true,
        tagName: 'a',

        html: '<svg class="pswp__download__btn" width="32" height="32" viewBox="0 0 32 32" fill="none"><g clip-path="url(#clip0_10929_39313)"><rect width="32" height="32" rx="16" fill="#FFD900"/><g clip-path="url(#clip1_10929_39313)"><path d="M8.99976 20.375V22.125C8.99976 22.5891 9.18413 23.0342 9.51232 23.3624C9.84051 23.6906 10.2856 23.875 10.7498 23.875H21.2498C21.7139 23.875 22.159 23.6906 22.4872 23.3624C22.8154 23.0342 22.9998 22.5891 22.9998 22.125V20.375" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.6248 15.125L15.9998 19.5L20.3748 15.125" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 9V19.5" stroke="#373E49" stroke-width="1.3125" stroke-linecap="round" stroke-linejoin="round"/></g></g><defs><clipPath id="clip0_10929_39313"><rect width="32" height="32" rx="16" fill="white"/></clipPath><clipPath id="clip1_10929_39313"><rect x="5.5" y="5.5" width="21" height="21" fill="white"/></clipPath></defs></svg>',

        onInit: (el, pswp) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');
          el.setAttribute('data-event-tracking', 'gensapp-photodrop-gallery-download-web');
          pswp.on('change', () => {
            const downloadUrl =
              pswp.currSlide.data.element?.dataset?.pswpDownloadLink || pswp.currSlide.data.src;

            el.removeEventListener('click', logEvent(el.href));
            el.href = downloadUrl;
            el.addEventListener('click', logEvent(el.href));
          });
        },
      });
    });
    lightbox.init();
    lightbox.on('openingAnimationStart', async () => {
      if (isIosNativeOnly()) {
        const pswp = document.getElementsByClassName('pswp__top-bar');
        pswp[0].style.height = '104px';
        pswp[0].style.paddingTop = '44px';
        await StatusBar.setStyle({ style: Style.Dark });
      }
    });
    lightbox.on('closingAnimationStart', async () => {
      if (isIosNativeOnly()) {
        await StatusBar.setStyle({ style: Style.Light });
      }
    });
    this.lightbox = lightbox;
  },
  unmounted() {
    window.removeEventListener('mousedown', this.onOutsideMenuClick);
    window.removeEventListener('touchstart', this.onOutsideMenuClick);
  },
  methods: {
    isPhotoSelected(photo) {
      return this.selectedPhotos.includes(photo);
    },
    formatDate(date) {
      momentts.tz.setDefault('Asia/Singapore');
      const dateObject = momentts(date);
      const currentLocale = this.$i18n.locale;
      if (currentLocale === 'zh') {
        return new Intl.DateTimeFormat('zh-CN', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(dateObject);
      } else if (currentLocale === 'th') {
        return new Intl.DateTimeFormat('th-TH', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(dateObject);
      } else {
        return dateObject.format('dddd, D MMMM YYYY');
      }
    },
    formatExpType(expType) {
      return this.expData[expType].title;
    },
    selectAllInGallery() {
      const previousState = this.isAllPhotosSelected;
      this.links.forEach((p, i) => {
        if (!previousState) {
          this.$store.dispatch('photos/addSelectedPhoto', p.url);
        } else {
          this.selectPhotos(p.url, i);
        }
      });
    },
    async selectPhotos(photo, index) {
      if (this.longPressConsumed) {
        this.longPressConsumed = false;
        return;
      }
      if (this.isSelectingPhotos) {
        await this.$store.dispatch('photos/toggleSelectedPhoto', photo);
      } else {
        this.lightbox.loadAndOpen(index, {
          gallery: document.querySelector('#' + this.galleryId),
        });
      }
    },
    onPhotoPointerDown(event, linkObj, index) {
      if (this.isSelectingPhotos) return;
      const touch = event.touches ? event.touches[0] : event;
      this.longPressStartX = touch.clientX;
      this.longPressStartY = touch.clientY;
      this.longPressHoldingIndex = index;
      this.longPressTimer = setTimeout(() => {
        this.openLongPressMenu(linkObj);
      }, 300);
    },
    onPhotoPointerMove(event) {
      if (this.longPressTimer === null) return;
      const touch = event.touches ? event.touches[0] : event;
      const dx = touch.clientX - this.longPressStartX;
      const dy = touch.clientY - this.longPressStartY;
      if (Math.sqrt(dx * dx + dy * dy) > 10) {
        this.cancelLongPress();
      }
    },
    onPhotoPointerUp() {
      this.cancelLongPress();
    },
    cancelLongPress() {
      if (this.longPressTimer !== null) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
      this.longPressHoldingIndex = null;
    },
    openLongPressMenu(linkObj) {
      this.longPressTimer = null;
      this.longPressHoldingIndex = null;
      this.longPressConsumed = true;
      this.longPressedPhoto = linkObj;
    },
    closeLongPressMenu() {
      this.longPressConsumed = false;
      this.longPressedPhoto = null;
    },
    async onLongPressDownload() {
      const photo = this.longPressedPhoto;
      this.closeLongPressMenu();
      const url = photo.type === 'video' ? photo.videoUrl || photo.url : photo.url;
      try {
        const fileName = new URL(url).pathname.split('/').pop();
        mixpanel.track('Photodrop Individual File Downloaded', {
          file_name: fileName,
          download_type: 'long_press_single',
          page_url: this.$route.path,
          page: 'photodrop',
        });
      } catch (e) {
        // noop
      }
      await this.downloadPhotos([{ url, type: photo.type }]);
    },
    onLongPressSelect() {
      const photo = this.longPressedPhoto;
      this.closeLongPressMenu();
      this.$store.dispatch('photos/updateIsSelectingPhotos', true);
      this.$store.dispatch('photos/addSelectedPhoto', photo.url);
    },
    outsideClick(event) {
      const { tooltip, tooltipTrigger } = this.$refs;

      if (!tooltip.contains(event.target) && event.target !== tooltipTrigger) {
        this.showingTooltip = false;
      }
    },
    toggleTooltip() {
      this.showingTooltip = !this.showingTooltip;
    },
    formatPhoto(photos) {
      const formatted = photos;
      for (const key in formatted) {
        formatted[key].w = 600;
        formatted[key].h = 600;
      }
      return formatted;
    },
  },
};
</script>

<style scoped>
.tiles {
  grid-template-columns: repeat(auto-fill, minmax(106px, 0));
}

@media (min-width: 768px) {
  .tiles {
    grid-template-columns: repeat(auto-fill, minmax(200px, 0));
  }
}

.tooltip-box {
  top: calc(100% + 14px);
  right: -10px;

  box-shadow: 0px 0px 5px rgba(11, 12, 15, 0.35);
  /* filter: drop-shadow(0px 0px 5px rgba(11, 12, 15, 0.35)); */
}

.tooltip-box:before {
  content: '';
  position: absolute;
  top: -7px;
  right: 10px;
  border: 6px solid #f8f9f9;
  border-color: transparent #f8f9f9 #f8f9f9 transparent;
  transform-origin: 0 0;
  transform: rotate(45deg);
  z-index: -1;
  box-shadow: -3px -3px 3px rgba(11, 12, 15, 0.15);
}
.tooltip-box:after {
  content: '';
  position: absolute;
  top: -7px;
  right: 10px;
  border: 6px solid white;
  border-color: #f8f9f9 transparent transparent #f8f9f9;
  transform-origin: 0 0;
  transform: rotate(45deg);
  z-index: 2;
}
</style>
